/**
 * Example usage:
 * 
 * <form x-data x-dirty-guard="{
 *     selectors: 'input, textarea',
 *     excludeSelectors: '[dirtyguard-exclude-dirty]',
 *     saveButton: '[dirtyguard-save-button]',
 *     bypassButton: '[dirtyguard-bypass-button]',
 *     resetOnSave: true,
 *     onDirtyChange: (isDirty) => { console.log('Dirty state:', isDirty); },
 *     confirmMessage: 'You have unsaved changes. Are you sure you want to leave?'
 * }">
 *     <input type="text" name="example" />
 *     <input type="text" name="ignored" dirtyguard-exclude-dirty />
 *     <button type="submit" dirtyguard-save-button>Save</button>
 * </form>
 * 
 * This directive will:
 * - Track changes to form fields.
 * - Warn the user if they try to navigate away with unsaved changes.
 * - Optionally reset the dirty state on save.
 * - Allow customization via the directive expression.
 * 
 * Simple usage:
 * 
 * <form x-data x-dirty-guard>
 *     <input type="text" name="example" />
 *     <button type="submit" dirtyguard-save-button>Save</button>
 * </form>
 */
const dirtyGuard = (el, { expression }, { evaluateLater, effect, cleanup }) => {
  let isDirty = false;
  let originalValues = new Map();

  // Parse configuration from expression
  const config = expression ? evaluate(expression) : {};
  const {
      selectors = 'input, select, textarea',
      excludeSelectors = '[dirtyguard-exclude-dirty]',
      saveButton = '[dirtyguard-save-button]',
      bypassButton = '[dirtyguard-bypass-button]',
      resetOnSave = true,
      onDirtyChange = null,
      confirmMessage = 'Changes you made may not be saved.'
  } = config;

  // Store initial values
  function captureInitialValues() {
      const inputs = el.querySelectorAll(selectors);
      originalValues.clear();
      
      inputs.forEach((input, index) => {
          if (input.matches && input.matches(excludeSelectors)) return;
          
          const key = input.name || input.id || `input-${index}`;
          let value = '';
          
          if (input.type === 'checkbox' || input.type === 'radio') {
              value = input.checked;
          } else if (input.type === 'file') {
              value = input.files ? Array.from(input.files).map(f => f.name).join(',') : '';
          } else {
              value = input.value;
          }
          
          originalValues.set(key, value);
      });
      
      setDirty(false);
  }

  function checkDirty() {
      const inputs = el.querySelectorAll(selectors);
      let dirty = false;
      
      inputs.forEach((input, index) => {
          if (input.matches && input.matches(excludeSelectors)) return;
          
          const key = input.name || input.id || `input-${index}`;
          const originalValue = originalValues.get(key);
          let currentValue = '';
          
          if (input.type === 'checkbox' || input.type === 'radio') {
              currentValue = input.checked;
          } else if (input.type === 'file') {
              currentValue = input.files ? Array.from(input.files).map(f => f.name).join(',') : '';
          } else {
              currentValue = input.value;
          }
          
          if (originalValue !== currentValue) {
              dirty = true;
          }
      });
      setDirty(dirty);
  }

  function setDirty(dirty) {
      if (isDirty !== dirty) {
          isDirty = dirty;
          el.setAttribute('data-is-dirty', dirty);
          
          if (onDirtyChange && typeof onDirtyChange === 'function') {
              onDirtyChange(dirty);
          }
      }
  }

  // Handle save button clicks
  function handleSaveClick(e) {
      if (resetOnSave) {
        setDirty(false)
        // Wait for Livewire to potentially update the DOM
          setTimeout(() => {
              captureInitialValues();
          }, 100);
      }
  }

  function handleBypassButtonClick() {
    setDirty(false)
  }

  // This is where the magic happens
  function handleBeforeUnload(e) {
    if (isDirty) {
        e.preventDefault();
        e.returnValue = confirmMessage;
        return confirmMessage; // Probably ignored by modern browsers
    }
  }

  function setupEventListeners() {
    el.addEventListener('input', checkDirty);
    el.addEventListener('change', checkDirty);
    
    const saveButtonEl = el.querySelector(saveButton);
    if (saveButtonEl) {
        saveButtonEl.addEventListener('click', handleSaveClick);
    }

    const bypassButtonEl = el.querySelectorAll(bypassButton);
    if (bypassButtonEl && bypassButtonEl.length) {
      bypassButtonEl.forEach(el => {
        if(el.handleBypassButtonClick) return
        el.handleBypassButtonClick = true
        el.addEventListener('click', handleBypassButtonClick);
      })
    }
    
    // Kick it off
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Reset dirty state after successful Livewire updates
    document.addEventListener('livewire:navigated', () => {
        setTimeout(captureInitialValues, 100);
    });
  }

  // Public API methods
  el._dirtyGuard = {
      isDirty: () => isDirty,
      setDirty: setDirty,
      reset: captureInitialValues,
      check: checkDirty
  };

  // Initialize
  captureInitialValues();
  setupEventListeners();

  cleanup(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (el._dirtyGuard) {
          delete el._dirtyGuard;
      }
  });
}

export default dirtyGuard;